/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Ref } from 'react'
import clsx from 'clsx'
import React, { useState, forwardRef } from 'react'
import { AsyncPaginate } from 'react-select-async-paginate'
import { isEmpty } from 'lodash'

import { Loader, Chip } from 'components'
import { IconSearch } from 'components/Icons'
import { getLabel } from 'helpers/language'
import { ILoaderOption } from 'types'

import { customStyles } from './style'

export interface AutoSuggestProps {
  message?: string
  placeholder?: string
  selected?: ILoaderOption
  className?: string
  options?: ILoaderOption[]
  loadOptions?: (
    searchQuery: string,
    loadedOptions: any,
    options: { page: number },
  ) => Promise<{
    options: any
    hasMore: any
    additional: { page: number }
  }>
  onBlur?: () => void
  onChange?: (item: ILoaderOption) => void
  height?: number
  position?: string
  fullWidth?: boolean
  isError?: boolean
  disabled?: boolean
  isMultiSelect?: boolean
  isSearchable?: boolean
  isEditBranch?: boolean
}

const AutoSuggest = (
  props: AutoSuggestProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element => {
  const {
    message,
    placeholder,
    selected,
    options,
    loadOptions,
    onBlur = () => {},
    onChange = () => {},
    isError,
    disabled,
    isMultiSelect,
    isSearchable,
  } = props

  const [value, setValue] = useState<ILoaderOption | null>(null)

  let selectedValue: ILoaderOption | null = selected || value

  if (isEmpty(selected?.key) && isEmpty(value)) {
    selectedValue = null
  }

  const handleChange = (item: unknown) => {
    setValue(item as ILoaderOption)
    onChange(item as ILoaderOption)
  }

  const IndicatorsContainerCustom = (props: any) => {
    const { isLoading } = props.selectProps

    return (
      <div className='select__indicators-container'>
        {isLoading ? <Loader size='sm' /> : <IconSearch variants='sm' />}
      </div>
    )
  }

  const MultiValueCustom = (props: any) => {
    return (
      <Chip
        className='chip__search__multi'
        onClose={props.removeProps.onClick}
        label={props.data.label}
        data-testid={`chip-${props.data.key}`}
      />
    )
  }

  return (
    <>
      <AsyncPaginate
        selectRef={ref}
        data-testid='react-select-async-pagination-component'
        classNamePrefix='custom-select'
        placeholder={placeholder}
        key={JSON.stringify(selected)}
        value={selectedValue}
        getOptionValue={(option) => option.key}
        getOptionLabel={(option) => option.label}
        options={options}
        loadOptions={loadOptions as never}
        styles={customStyles(props)}
        onBlur={onBlur}
        onChange={handleChange}
        additional={{
          page: 1,
        }}
        components={{
          MultiValue: MultiValueCustom,
          IndicatorsContainer: IndicatorsContainerCustom,
          IndicatorSeparator: null,
        }}
        debounceTimeout={300}
        isError={isError}
        isDisabled={disabled}
        isMulti={isMultiSelect}
        isSearchable={isSearchable}
      />
      {message && (
        <span
          className={clsx('text-input__message', {
            'text-input__error': isError,
          })}
          data-testid='text-input-message'
        >
          {getLabel(message)}
        </span>
      )}
    </>
  )
}

export default forwardRef(AutoSuggest)
